/**
 * User Service - Simplified unified user management
 * Replaces the complex profileService and loan officer profile logic
 */

import { supabase as defaultSupabase } from '@/utils/supabase';
import { SupabaseClient } from '@supabase/supabase-js';
import {
  User,
  UserDB,
  RosterUser,
  RosterUserDB,
  transformRosterUserFromDB,
  transformUserFromDB,
  transformUserToDB
} from '@/types/user';
import { ServiceResponse, ServiceError } from '@/types/services';
import { ErrorCode, ErrorFactory } from '@/types/errors';
import { createComponentLogger } from '@/utils/logger';
import { COMPANY_SIGNATURE_SOCIALS, normalizeCompanySocialLink } from '../../supabase/functions/_shared/company-signature-links';

const log = createComponentLogger('UserService', 'users');

const TWILIO_ASSIGNMENT_DB_FIELDS: Array<keyof UserDB> = [
  'business_sms_number',
  'twilio_phone_sid',
  'twilio_number_status',
  'twilio_provisioned_at',
  'twilio_released_at'
];

export interface UserReadOptions {
  signal?: AbortSignal;
  supabaseClient?: SupabaseClient;
}

function stripTwilioAssignmentFields<T extends Partial<UserDB>>(dbUser: T): T {
  // Twilio number ownership is managed by dedicated Twilio assignment flows.
  // Generic profile/user writes must not clear or seed those fields.
  TWILIO_ASSIGNMENT_DB_FIELDS.forEach((field) => {
    delete dbUser[field];
  });
  return dbUser;
}

export class UserService {
  /**
   * Get a single user by ID
   */
  static async getUser(userId: string, supabaseClient: SupabaseClient = defaultSupabase): Promise<ServiceResponse<User>> {
    try {
      const { data: { session }, error: sessionError } =
        await supabaseClient.auth.getSession();
      if (sessionError || !session) {
        return {
          data: null,
          error: ErrorFactory.auth(
            ErrorCode.USER_NOT_AUTHENTICATED,
            sessionError?.message ?? 'Not authenticated'
          )
        };
      }

      const { data, error } = await supabaseClient
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        log.error('Failed to fetch user', { userId, error });
        return { data: null, error };
      }

      return { 
        data: data ? transformUserFromDB(data as UserDB) : null, 
        error: null 
      };
    } catch (error) {
      log.error('Exception fetching user', { userId, error });
      return { data: null, error };
    }
  }

  /**
   * Get user by email
   */
  static async getUserByEmail(email: string, supabaseClient: SupabaseClient = defaultSupabase): Promise<ServiceResponse<User>> {
    try {
      const { data, error } = await supabaseClient
        .from('users')
        .select('*')
        .eq('email', email.toLowerCase())
        .single();

      if (error) {
        log.error('Failed to fetch user by email', { email, error });
        return { data: null, error };
      }

      return { 
        data: data ? transformUserFromDB(data as UserDB) : null, 
        error: null 
      };
    } catch (error) {
      log.error('Exception fetching user by email', { email, error });
      return { data: null, error };
    }
  }

  /**
   * Get all users in an organization
   */
  static async getOrganizationUsers(
    organizationId: string,
    options: UserReadOptions = {}
  ): Promise<{ data: User[]; error: ServiceError | null }> {
    const {
      signal,
      supabaseClient = defaultSupabase
    } = options;

    try {
      let query = supabaseClient
        .from('users')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('employment_status', 'active')
        .order('last_name', { ascending: true });

      if (signal) {
        query = query.abortSignal(signal);
      }

      const { data, error } = await query;
      if (signal?.aborted) {
        return { data: [], error: null };
      }

      if (error) {
        log.error('Failed to fetch organization users', { organizationId, error });
        return { data: [], error };
      }

      const users = (data || []).map(u => transformUserFromDB(u as UserDB));
      return { data: users, error: null };
    } catch (error) {
      if (signal?.aborted) {
        return { data: [], error: null };
      }
      log.error('Exception fetching organization users', { organizationId, error });
      return { data: [], error };
    }
  }

  /**
   * Get the employee-directory roster for the caller's own organization.
   *
   * Every active employee can read the whole roster, which is why this goes
   * through the `get_organization_roster` RPC instead of selecting
   * public.users: that table also carries compensation and Encompass columns,
   * and RLS is row-level, so an org-wide policy would expose them too. The RPC
   * derives the organization from the caller's session and returns only the
   * directory projection.
   */
  static async getOrganizationRoster(
    options: UserReadOptions = {}
  ): Promise<{ data: RosterUser[]; error: Error | null }> {
    const {
      signal,
      supabaseClient = defaultSupabase
    } = options;

    try {
      let query = supabaseClient.rpc('get_organization_roster');

      if (signal) {
        query = query.abortSignal(signal);
      }

      const { data, error } = await query;
      if (signal?.aborted) {
        return { data: [], error: null };
      }

      if (error) {
        const rosterError = new Error(error.message);
        log.error('Failed to fetch organization roster', {}, rosterError);
        return { data: [], error: rosterError };
      }

      const roster = ((data || []) as RosterUserDB[]).map(transformRosterUserFromDB);
      return { data: roster, error: null };
    } catch (caught) {
      if (signal?.aborted) {
        return { data: [], error: null };
      }
      const rosterError = caught instanceof Error
        ? caught
        : new Error(String(caught));
      log.error('Exception fetching organization roster', {}, rosterError);
      return { data: [], error: rosterError };
    }
  }

  /**
   * Get all loan officers in an organization
   */
  static async getLoanOfficers(organizationId: string, supabaseClient: SupabaseClient = defaultSupabase): Promise<{ data: User[]; error: ServiceError | null }> {
    try {
      const { data, error } = await supabaseClient
        .from('users')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('is_loan_officer', true)
        .eq('employment_status', 'active')
        .order('last_name', { ascending: true });

      if (error) {
        log.error('Failed to fetch loan officers', { organizationId, error });
        return { data: [], error };
      }

      const users = (data || []).map(u => transformUserFromDB(u as UserDB));
      return { data: users, error: null };
    } catch (error) {
      log.error('Exception fetching loan officers', { organizationId, error });
      return { data: [], error };
    }
  }

  /**
   * Get current user's profile
   * @param supabaseClient - Optional authenticated Supabase client
   */
  static async getCurrentUser(supabaseClient: SupabaseClient = defaultSupabase): Promise<ServiceResponse<User>> {
    try {
      const { data: { user: authUser }, error: authError } = await supabaseClient.auth.getUser();
      
      if (authError || !authUser) {
        return {
          data: null,
          error: ErrorFactory.auth(
            ErrorCode.USER_NOT_AUTHENTICATED,
            authError?.message ?? 'Not authenticated'
          )
        };
      }

      return this.getUser(authUser.id, supabaseClient);
    } catch (error) {
      log.error('Exception fetching current user', { error });
      return { data: null, error };
    }
  }

  /**
   * Update a user's profile
   */
  static async updateUser(userId: string, updates: Partial<User>, supabaseClient: SupabaseClient = defaultSupabase): Promise<ServiceResponse<User>> {
    try {
      // Validate userId exists first
      if (!userId) {
        log.error('Invalid userId provided for update');
        return { data: null, error: 'User ID is required' };
      }

      const dbUpdates = stripTwilioAssignmentFields(transformUserToDB(updates));
      
      // Debug logging disabled for cleaner console
      
      // Debug logging to see what's being sent
      log.info('Updating user with data:', { 
        userId, 
        applicationFields: {
          applicationUserId: updates.applicationUserId,
          applicationLink: updates.applicationLink,
          applicationLinkGeneratedAt: updates.applicationLinkGeneratedAt
        },
        dbApplicationFields: {
          application_user_id: dbUpdates.application_user_id,
          application_link: dbUpdates.application_link,
          application_link_generated_at: dbUpdates.application_link_generated_at
        }
      });
      
      // Step 1: Perform update WITH select to get count
      // Debug: Check if session exists before update
      const { data: { session: _currentSession } } = await supabaseClient.auth.getSession();
      // Session verified

      const { data: updateData, error: updateError, count } = await supabaseClient
        .from('users')
        .update(dbUpdates)
        .eq('id', userId)
        .select('id'); // Need select() to get count of affected rows

      // Update response received

      if (updateError) {
        // Update failed
        log.error('Failed to update user', { userId, error: updateError });
        return { data: null, error: updateError };
      }

      // Verify update actually occurred
      if (!updateData || updateData.length === 0) {
        // No rows updated - likely RLS policy blocking
        log.warn('No rows updated - RLS blocked', { userId, count });
        return { data: null, error: 'Access denied - authentication context missing' };
      }
      
      // Update successful

      // Step 2: Attempt to read back (may fail due to RLS)
      const { data, error: selectError } = await supabaseClient
        .from('users')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (selectError || !data) {
        // UPDATE succeeded but SELECT failed - this is actually OK
        log.warn('User updated but cannot read due to RLS', { 
          userId, 
          selectError,
          updateSucceeded: true 
        });
        
        // Return partial success with minimal data
        // Caller can decide how to handle this case
        return { 
          data: { id: userId, ...updates } as User, // Return what we sent
          error: null 
        };
      }

      log.info('User updated successfully', { userId });
      return { 
        data: transformUserFromDB(data as UserDB), 
        error: null 
      };
    } catch (error) {
      log.error('Exception updating user', { userId, error });
      return { data: null, error };
    }
  }

  /**
   * Create a new user
   */
  static async createUser(userData: Partial<User>, supabaseClient: SupabaseClient = defaultSupabase): Promise<ServiceResponse<User>> {
    try {
      // Validate required fields
      if (!userData.email) {
        return { data: null, error: 'Email is required' };
      }

      const dbUser = stripTwilioAssignmentFields(transformUserToDB(userData));
      
      const { data, error } = await supabaseClient
        .from('users')
        .insert(dbUser)
        .select()
        .single();

      if (error) {
        log.error('Failed to create user', { error });
        return { data: null, error };
      }

      log.info('User created successfully', { email: userData.email });
      return { 
        data: data ? transformUserFromDB(data as UserDB) : null, 
        error: null 
      };
    } catch (error) {
      log.error('Exception creating user', { error });
      return { data: null, error };
    }
  }

  /**
   * Soft delete a user (set deleted_at)
   */
  static async deleteUser(userId: string, supabaseClient: SupabaseClient = defaultSupabase): Promise<{ success: boolean; error: ServiceError | null }> {
    try {
      const { error } = await supabaseClient
        .from('users')
        .update({
          deleted_at: new Date().toISOString(),
          employment_status: 'terminated'
        })
        .eq('id', userId);

      if (error) {
        log.error('Failed to delete user', { userId, error });
        return { success: false, error };
      }

      log.info('User soft deleted', { userId });
      return { success: true, error: null };
    } catch (error) {
      log.error('Exception deleting user', { userId, error });
      return { success: false, error };
    }
  }

  /**
   * Activate/deactivate a user
   */
  static async setUserActive(userId: string, isActive: boolean, supabaseClient: SupabaseClient = defaultSupabase): Promise<{ success: boolean; error: ServiceError | null }> {
    try {
      const { error } = await supabaseClient
        .from('users')
        .update({ employment_status: isActive ? 'active' : 'terminated' })
        .eq('id', userId);

      if (error) {
        log.error('Failed to update user active status', { userId, isActive, error });
        return { success: false, error };
      }

      log.info('User active status updated', { userId, isActive });
      return { success: true, error: null };
    } catch (error) {
      log.error('Exception updating user active status', { userId, error });
      return { success: false, error };
    }
  }

  /**
   * Update user roles
   */
  static async updateUserRoles(userId: string, roles: string[], supabaseClient: SupabaseClient = defaultSupabase): Promise<{ success: boolean; error: ServiceError | null }> {
    try {
      const { error } = await supabaseClient
        .from('users')
        .update({ roles })
        .eq('id', userId);

      if (error) {
        log.error('Failed to update user roles', { userId, roles, error });
        return { success: false, error };
      }

      log.info('User roles updated', { userId, roles });
      return { success: true, error: null };
    } catch (error) {
      log.error('Exception updating user roles', { userId, error });
      return { success: false, error };
    }
  }

  /**
   * Search users by name or email
   */
  static async searchUsers(query: string, organizationId?: string, supabaseClient: SupabaseClient = defaultSupabase): Promise<{ data: User[]; error: ServiceError | null }> {
    try {
      let queryBuilder = supabaseClient
        .from('users')
        .select('*')
        .or(`first_name.ilike.%${query}%,last_name.ilike.%${query}%,email.ilike.%${query}%`)
        .eq('employment_status', 'active');

      if (organizationId) {
        queryBuilder = queryBuilder.eq('organization_id', organizationId);
      }

      const { data, error } = await queryBuilder
        .order('last_name', { ascending: true })
        .limit(50);

      if (error) {
        log.error('Failed to search users', { query, error });
        return { data: [], error };
      }

      const users = (data || []).map(u => transformUserFromDB(u as UserDB));
      return { data: users, error: null };
    } catch (error) {
      log.error('Exception searching users', { query, error });
      return { data: [], error };
    }
  }

  /**
   * Get users with incomplete user data
   */
  static async getIncompleteProfiles(organizationId: string, supabaseClient: SupabaseClient = defaultSupabase): Promise<{ data: User[]; error: ServiceError | null }> {
    try {
      const { data, error } = await supabaseClient
        .from('users')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('profile_complete', false)
        .eq('employment_status', 'active')
        .order('created_at', { ascending: false });

      if (error) {
        log.error('Failed to fetch incomplete users', { organizationId, error });
        return { data: [], error };
      }

      const users = (data || []).map(u => transformUserFromDB(u as UserDB));
      return { data: users, error: null };
    } catch (error) {
      log.error('Exception fetching incomplete users', { organizationId, error });
      return { data: [], error };
    }
  }

  /**
   * Update user's profile photo
   */
  static async updateProfilePhoto(
    userId: string,
    photoUrl: string,
    photoType: 'profile_photo' | 'profile_photo_facial' | 'profile_photo_body' = 'profile_photo'
  ): Promise<{ success: boolean; error: ServiceError | null }> {
    try {
      const { error } = await defaultSupabase
        .from('users')
        .update({ [photoType]: photoUrl })
        .eq('id', userId);

      if (error) {
        log.error('Failed to update profile photo', { userId, photoType, error });
        return { success: false, error };
      }

      log.info('Profile photo updated', { userId, photoType });
      return { success: true, error: null };
    } catch (error) {
      log.error('Exception updating profile photo', { userId, error });
      return { success: false, error };
    }
  }

  /**
   * Update user's notification preferences
   */
  static async updateNotificationPreferences(
    userId: string,
    preferences: unknown
  ): Promise<{ success: boolean; error: ServiceError | null }> {
    try {
      const { error } = await defaultSupabase
        .from('users')
        .update({ notification_preferences: preferences })
        .eq('id', userId);

      if (error) {
        log.error('Failed to update notification preferences', { userId, error });
        return { success: false, error };
      }

      log.info('Notification preferences updated', { userId });
      return { success: true, error: null };
    } catch (error) {
      log.error('Exception updating notification preferences', { userId, error });
      return { success: false, error };
    }
  }

  /**
   * Check if email is available
   */
  static async isEmailAvailable(email: string, excludeUserId?: string, supabaseClient: SupabaseClient = defaultSupabase): Promise<boolean> {
    try {
      let query = supabaseClient
        .from('users')
        .select('id')
        .eq('email', email.toLowerCase());

      if (excludeUserId) {
        query = query.neq('id', excludeUserId);
      }

      const { data } = await query;
      return !data || data.length === 0;
    } catch (error) {
      log.error('Exception checking email availability', { email, error });
      return false;
    }
  }

  /**
   * Check if website slug is available
   */
  static async isWebsiteSlugAvailable(slug: string, excludeUserId?: string, supabaseClient: SupabaseClient = defaultSupabase): Promise<boolean> {
    try {
      let query = supabaseClient
        .from('users')
        .select('id')
        .eq('website_slug', slug.toLowerCase());

      if (excludeUserId) {
        query = query.neq('id', excludeUserId);
      }

      const { data } = await query;
      return !data || data.length === 0;
    } catch (error) {
      log.error('Exception checking website slug availability', { slug, error });
      return false;
    }
  }

  /**
   * Get all loan officers (for backward compatibility with hooks)
   * Returns loan officers from the current user's organization
   */
  static async getAllLoanOfficers(supabaseClient: SupabaseClient = defaultSupabase): Promise<User[]> {
    try {
      // Get current user's organization
      const { data: { user: authUser } } = await supabaseClient.auth.getUser();
      if (!authUser) {
return [];
}
      
      const { data: currentUser } = await supabaseClient
        .from('users')
        .select('organization_id')
        .eq('id', authUser.id)
        .single();
      
      if (!currentUser?.organization_id) {
return [];
}
      
      // Get loan officers from the same organization
      const { data, error } = await this.getLoanOfficers(currentUser.organization_id);
      return error ? [] : data;
    } catch (error) {
      log.error('Exception in getAllLoanOfficers', { error });
      return [];
    }
  }

  /**
   * Company Profile methods (temporary stubs until full migration)
   * These will eventually be stored in the users table or a separate config
   */
  
  static async getCurrentCompanyProfile(supabaseClient: SupabaseClient = defaultSupabase): Promise<CompanyProfile | null> {
    try {
      // Get current authenticated user
      const { data: { user }, error: authError } = await supabaseClient.auth.getUser();

      if (authError || !user) {
        log.error('Failed to get authenticated user', { error: authError });
        return null;
      }

      // Get user's profile with organization_id
      const { data: profile, error: profileError } = await supabaseClient
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (profileError || !profile?.organization_id) {
        log.error('Failed to get user profile or organization_id', { error: profileError, userId: user.id });
        return null;
      }

      // Get company profile data from company_profiles table
      const { data: companyProfile, error: companyError } = await supabaseClient
        .from('company_profiles')
        .select('*')
        .eq('organization_id', profile.organization_id)
        .single();

      if (companyError || !companyProfile) {
        log.error('Failed to get company profile data', { error: companyError, organizationId: profile.organization_id });
        return null;
      }

      // Get encompass settings from organizations table if needed
      const { data: org } = await supabaseClient
        .from('organizations')
        .select('encompass_instance_id, encompass_sync_enabled, encompass_last_sync, encompass_sync_schedule, encompass_sync_settings')
        .eq('id', profile.organization_id)
        .single();

      // Transform company_profiles data to CompanyProfile format
      log.info('Successfully fetched company profile', { organizationId: profile.organization_id, name: companyProfile.company_name });

      return {
        _id: companyProfile.id,
        organization_id: companyProfile.organization_id,
        company_name: companyProfile.company_name || 'Unknown Company',
        legal_name: companyProfile.legal_name || companyProfile.company_name || 'Unknown Legal Name',
        dba_names: companyProfile.dba_names || [],
        company_nmls_id: companyProfile.company_nmls_id || '',
        state_licenses: companyProfile.state_licenses || [],
        federal_ein: companyProfile.federal_ein,
        headquarters_address: companyProfile.headquarters_address || {
          street: '',
          city: '',
          state: '',
          zip: ''
        },
        main_phone: companyProfile.main_phone || '',
        toll_free_phone: companyProfile.toll_free_phone,
        fax: companyProfile.fax,
        main_email: companyProfile.main_email || '',
        support_email: companyProfile.support_email,
        website: companyProfile.website || '',
        company_mortgage_application_link: companyProfile.company_mortgage_application_link,
        logo_url: companyProfile.logo_url,
        logo_light_url: companyProfile.logo_light_url,
        favicon_url: companyProfile.favicon_url,
        equal_housing_lender: companyProfile.equal_housing_lender !== false,
        default_disclaimers: companyProfile.default_disclaimers || [],
        pre_approval_disclaimer: companyProfile.pre_approval_disclaimer,
        google_review_url: companyProfile.google_review_url,
        facebook_url: companyProfile.facebook_url,
        instagram_url: companyProfile.instagram_url,
        linkedin_url: companyProfile.linkedin_url,
        tiktok_url: companyProfile.tiktok_url,
        encompass_instance_id: org?.encompass_instance_id,
        encompass_sync_enabled: org?.encompass_sync_enabled || false,
        encompass_last_sync: org?.encompass_last_sync,
        encompass_sync_schedule: org?.encompass_sync_schedule || 'manual',
        encompass_sync_settings: org?.encompass_sync_settings || {},
        created_at: companyProfile.created_at || new Date().toISOString(),
        updated_at: companyProfile.updated_at || new Date().toISOString()
      };
    } catch (error) {
      log.error('Exception in getCurrentCompanyProfile', { error });
      return null;
    }
  }

  static async updateCompanyProfile(updates: Partial<CompanyProfile>, supabaseClient: SupabaseClient = defaultSupabase): Promise<CompanyProfile | null> {
    try {
      // Get current profile to get organization_id and company profile id
      const current = await this.getCurrentCompanyProfile(supabaseClient);
      if (!current || !current.organization_id) {
        log.error('No current company profile found');
        return null;
      }

      // Prepare update data for company_profiles table
      const profileUpdates: Record<string, unknown> = {};

      // Map CompanyProfile fields to company_profiles table columns
      if (updates.company_name !== undefined) {
profileUpdates.company_name = updates.company_name;
}
      if (updates.legal_name !== undefined) {
profileUpdates.legal_name = updates.legal_name;
}
      if (updates.dba_names !== undefined) {
profileUpdates.dba_names = updates.dba_names;
}
      if (updates.company_nmls_id !== undefined) {
profileUpdates.company_nmls_id = updates.company_nmls_id;
}
      if (updates.state_licenses !== undefined) {
profileUpdates.state_licenses = updates.state_licenses;
}
      if (updates.federal_ein !== undefined) {
profileUpdates.federal_ein = updates.federal_ein;
}
      if (updates.headquarters_address !== undefined) {
profileUpdates.headquarters_address = updates.headquarters_address;
}
      if (updates.main_phone !== undefined) {
profileUpdates.main_phone = updates.main_phone;
}
      if (updates.toll_free_phone !== undefined) {
profileUpdates.toll_free_phone = updates.toll_free_phone;
}
      if (updates.fax !== undefined) {
profileUpdates.fax = updates.fax;
}
      if (updates.main_email !== undefined) {
profileUpdates.main_email = updates.main_email;
}
      if (updates.support_email !== undefined) {
profileUpdates.support_email = updates.support_email;
}
      if (updates.website !== undefined) {
profileUpdates.website = updates.website;
}
      if (updates.company_mortgage_application_link !== undefined) {
profileUpdates.company_mortgage_application_link = updates.company_mortgage_application_link;
}
      if (updates.logo_url !== undefined) {
profileUpdates.logo_url = updates.logo_url;
}
      if (updates.logo_light_url !== undefined) {
profileUpdates.logo_light_url = updates.logo_light_url;
}
      if (updates.favicon_url !== undefined) {
profileUpdates.favicon_url = updates.favicon_url;
}
      if (updates.equal_housing_lender !== undefined) {
profileUpdates.equal_housing_lender = updates.equal_housing_lender;
}
      if (updates.default_disclaimers !== undefined) {
profileUpdates.default_disclaimers = updates.default_disclaimers;
}
      if (updates.pre_approval_disclaimer !== undefined) {
profileUpdates.pre_approval_disclaimer = updates.pre_approval_disclaimer;
}
      for (const { field } of COMPANY_SIGNATURE_SOCIALS) {
        if (updates[field] !== undefined) {
          profileUpdates[field] = normalizeCompanySocialLink(updates[field]);
        }
      }
      if (updates.google_review_url !== undefined) {
        // Normalize blank/empty input to null so admins can clear the field
        // without violating the company_profiles_google_review_url_valid CHECK constraint.
        const trimmed = (updates.google_review_url ?? '').trim();
        profileUpdates.google_review_url = trimmed.length > 0 ? trimmed : null;
      }

      // Add updated_at timestamp
      profileUpdates.updated_at = new Date().toISOString();

      // Update the company profile
      const { error: profileError } = await supabaseClient
        .from('company_profiles')
        .update(profileUpdates)
        .eq('organization_id', current.organization_id)
        .select()
        .single();

      if (profileError) {
        log.error('Failed to update company profile', { error: profileError, organizationId: current.organization_id });
        return null;
      }

      // Update Encompass fields in organizations table if present
      const orgUpdates: Record<string, unknown> = {};
      let hasOrgUpdates = false;

      if (updates.encompass_instance_id !== undefined) {
        orgUpdates.encompass_instance_id = updates.encompass_instance_id;
        hasOrgUpdates = true;
      }
      if (updates.encompass_sync_enabled !== undefined) {
        orgUpdates.encompass_sync_enabled = updates.encompass_sync_enabled;
        hasOrgUpdates = true;
      }
      if (updates.encompass_sync_schedule !== undefined) {
        orgUpdates.encompass_sync_schedule = updates.encompass_sync_schedule;
        hasOrgUpdates = true;
      }
      if (updates.encompass_sync_settings !== undefined) {
        orgUpdates.encompass_sync_settings = updates.encompass_sync_settings;
        hasOrgUpdates = true;
      }

      if (hasOrgUpdates) {
        orgUpdates.updated_at = new Date().toISOString();
        const { error: orgError } = await supabaseClient
          .from('organizations')
          .update(orgUpdates)
          .eq('id', current.organization_id);

        if (orgError) {
          log.warn('Failed to update organization encompass settings', { error: orgError, organizationId: current.organization_id });
          // Don't fail the entire update if only encompass settings fail
        }
      }

      log.info('Successfully updated company profile', { organizationId: current.organization_id });

      // Return the updated profile in the expected format
      return await this.getCurrentCompanyProfile(supabaseClient);
    } catch (error) {
      log.error('Exception in updateCompanyProfile', { error });
      return null;
    }
  }

  static async createCompanyProfile(companyData: Partial<CompanyProfile>): Promise<CompanyProfile | null> {
    log.warn('createCompanyProfile called - operation not yet implemented');
    // Return stub data for now
    const stub = await this.getCurrentCompanyProfile();
    return stub ? { ...stub, ...companyData } : null;
  }

  /**
   * Branch Office methods (temporary stubs until full migration)
   */

  static async getAllBranchOffices(supabaseClient: SupabaseClient = defaultSupabase): Promise<BranchOffice[]> {
    try {
      // Get current user's organization
      const { data: { user: authUser } } = await supabaseClient.auth.getUser();
      if (!authUser) {
        log.debug('No authenticated user - returning empty array');
        return [];
      }

      const { data: currentUser } = await supabaseClient
        .from('users')
        .select('organization_id')
        .eq('id', authUser.id)
        .single();

      if (!currentUser?.organization_id) {
        log.debug('No organization_id found for user - returning empty array');
        return [];
      }

      // Get branch offices from the same organization
      const { data, error } = await supabaseClient
        .from('branch_offices')
        .select('*')
        .eq('organization_id', currentUser.organization_id)
        .order('branch_name');

      if (error) {
        log.error('Failed to fetch branch offices', { error, organizationId: currentUser.organization_id });
        return [];
      }

      // Transform database format to BranchOffice interface
      const branches: BranchOffice[] = (data || []).map(branch => ({
        _id: branch.id,
        id: branch.id,
        organization_id: branch.organization_id,
        company_profile_id: branch.company_profile_id || '',
        branch_name: branch.branch_name,
        branch_code: branch.branch_code,
        is_headquarters: branch.is_headquarters || false,
        address: branch.address,
        phone: branch.phone,
        fax: branch.fax,
        email: branch.email,
        branch_manager_id: branch.branch_manager_id,
        is_active: branch.is_active ?? true,
        created_at: branch.created_at,
        updated_at: branch.updated_at
      }));

      log.info('Successfully fetched branch offices', { count: branches.length, organizationId: currentUser.organization_id });
      return branches;
    } catch (error) {
      log.error('Exception in getAllBranchOffices', { error });
      return [];
    }
  }

  static async createBranchOffice(branch: Omit<BranchOffice, 'id' | 'created_at' | 'updated_at'>): Promise<BranchOffice | null> {
    log.warn('createBranchOffice called - operation not yet implemented');
    const branchId = `temp-branch-${Date.now()}`;
    return {
      ...branch,
      _id: branchId,
      id: branchId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  }

  static async updateBranchOffice(_id: string, updates: Partial<BranchOffice>): Promise<BranchOffice | null> {
    log.warn('updateBranchOffice called - operation not yet implemented');
    return {
      _id,
      organization_id: 'default-org',
      company_profile_id: 'default-company',
      branch_name: 'Main Branch',
      is_headquarters: true,
      address: {
        street: '123 Main St',
        city: 'Los Angeles',
        state: 'CA',
        zip: '90001'
      },
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      ...updates
    };
  }

  static async deleteBranchOffice(_id: string): Promise<void> {
    log.warn('deleteBranchOffice called - operation not yet implemented');
    return Promise.resolve();
  }

  /**
   * Template variables for PDF generation
   */
  static async getTemplateVariables() {
    const user = await this.getCurrentUser();
    const company = await this.getCurrentCompanyProfile();
    
    // Get the photo URL from the user profile - facial with body fallback (NO LEGACY)
    const profilePhoto = user?.data?.profilePhotoFacial || 
                        user?.data?.profilePhotoBody || 
                        '';
    
    // Return flat structure for template variables
    return {
      // Loan Officer Information
      loanOfficerName: user?.data ? `${user.data.firstName} ${user.data.lastName}` : '',
      loanOfficerFirstName: user?.data?.firstName || '',
      loanOfficerLastName: user?.data?.lastName || '',
      loanOfficerTitle: user?.data?.title || 'Loan Officer',
      loanOfficerEmail: user?.data?.email || '',
      loanOfficerPhone: user?.data?.mobilePhone || user?.data?.phone || '',
      loanOfficerMobile: user?.data?.mobilePhone || user?.data?.phone || '',
      loanOfficerNMLS: user?.data?.nmlsNumber ? `NMLS# ${user.data.nmlsNumber}` : '',
      loanOfficerNMLSRaw: user?.data?.nmlsNumber || '',  // Just the number without prefix
      loanOfficerPhoto: profilePhoto,
      profile_photo_url: profilePhoto,  // Add this for the template image element
      loanOfficerWebsite: user?.data?.website || '',
      loanOfficerSignature: user?.data?.signatureImage || '',
      
      // Company Information
      companyName: company?.company_name || '',
      companyNMLS: company?.company_nmls_id ? `NMLS# ${company.company_nmls_id}` : '',
      companyPhone: company?.main_phone || '',
      companyEmail: company?.main_email || '',
      companyWebsite: company?.website || '',
      companyAddress: company?.headquarters_address ? {
        street: company.headquarters_address.street || '',
        city: company.headquarters_address.city || '',
        state: company.headquarters_address.state || '',
        zip: company.headquarters_address.zip || ''
      } : {
        street: '',
        city: '',
        state: '',
        zip: ''
      },
      
      // Legacy nested structure (for backward compatibility)
      loanOfficer: user?.data ? {
        name: `${user.data.firstName} ${user.data.lastName}`,
        title: user.data.title || 'Loan Officer',
        email: user.data.email,
        phone: user.data.phone || '',
        nmls_number: user.data.nmls || '',
        profile_photo_url: profilePhoto
      } : null,
      company: company ? {
        name: company.company_name,
        logo_url: company.logo_url || '',
        address: company.headquarters_address,
        phone: company.main_phone,
        email: company.main_email,
        website: company.website,
        nmls_id: company.company_nmls_id,
        equal_housing_lender: company.equal_housing_lender
      } : null
    };
  }

  /**
   * Get template variables for a SPECIFIC user (for borrower-facing documents)
   * Use this when you need loan officer info for a pre-approval created by someone else
   * @param userId - The UUID of the loan officer whose info should be displayed
   */
  static async getTemplateVariablesForUser(userId: string) {
    // Get SPECIFIC user's profile (not current auth user)
    const { data: user } = await this.getUser(userId);

    // Get company profile using the loan officer's organization_id
    // (cannot use getCurrentCompanyProfile as that requires auth user)
    let company: CompanyProfile | null = null;
    if (user?.organizationId) {
      try {
        const { data: companyProfile } = await defaultSupabase
          .from('company_profiles')
          .select('*')
          .eq('organization_id', user.organizationId)
          .single();
        company = companyProfile;
      } catch (error) {
        log.error('Failed to fetch company profile for organization', { organizationId: user.organizationId, error });
      }
    }

    // Get the photo URL from the user profile - facial with body fallback
    const profilePhoto = user?.profilePhotoFacial ||
                        user?.profilePhotoBody ||
                        '';

    // Return flat structure for template variables
    return {
      // Loan Officer Information
      loanOfficerName: user ? `${user.firstName} ${user.lastName}` : '',
      loanOfficerFirstName: user?.firstName || '',
      loanOfficerLastName: user?.lastName || '',
      loanOfficerTitle: user?.title || 'Loan Officer',
      loanOfficerEmail: user?.email || '',
      loanOfficerPhone: user?.mobilePhone || user?.phone || '',
      loanOfficerMobile: user?.mobilePhone || user?.phone || '',
      loanOfficerNMLS: user?.nmlsNumber ? `NMLS# ${user.nmlsNumber}` : '',
      loanOfficerNMLSRaw: user?.nmlsNumber || '',
      loanOfficerPhoto: profilePhoto,
      profile_photo_url: profilePhoto,
      loanOfficerWebsite: user?.website || '',
      loanOfficerSignature: user?.signatureImage || '',

      // Company Information (from profile service)
      companyName: company?.company_name || '',
      companyNMLS: company?.company_nmls_id ? `NMLS# ${company.company_nmls_id}` : '',
      companyPhone: company?.main_phone || '',
      companyEmail: company?.main_email || '',
      companyWebsite: company?.website || '',
      companyAddress: company?.headquarters_address ? {
        street: company.headquarters_address.street || '',
        city: company.headquarters_address.city || '',
        state: company.headquarters_address.state || '',
        zip: company.headquarters_address.zip || ''
      } : {
        street: '',
        city: '',
        state: '',
        zip: ''
      },

      // Legacy nested structure (for backward compatibility)
      loanOfficer: user ? {
        name: `${user.firstName} ${user.lastName}`,
        title: user.title || 'Loan Officer',
        email: user.email,
        phone: user.phone || '',
        nmls_number: user.nmls || '',
        profile_photo_url: profilePhoto
      } : null,
      company: company ? {
        name: company.company_name,
        logo_url: company.logo_url || '',
        address: company.headquarters_address,
        phone: company.main_phone,
        email: company.main_email,
        website: company.website,
        nmls_id: company.company_nmls_id,
        equal_housing_lender: company.equal_housing_lender
      } : null
    };
  }

  /**
   * Get all users from the unified users table
   */
  static async getAllUsers(options: UserReadOptions = {}): Promise<User[]> {
    const {
      signal,
      supabaseClient = defaultSupabase
    } = options;

    try {
      let query = supabaseClient
        .from('users')
        .select(`
          *,
          organization:organizations(id, name)
        `)
        .eq('employment_status', 'active')
        .order('email');

      if (signal) {
        query = query.abortSignal(signal);
      }

      const { data, error } = await query;
      if (signal?.aborted) {
        return [];
      }

      if (error) {
        throw error;
      }

      return (data || []).map(u => transformUserFromDB(u as UserDB));
    } catch (error) {
      if (signal?.aborted) {
        return [];
      }
      log.error('Exception fetching all users', { error });
      throw error;
    }
  }

  /**
   * @deprecated Use getAllUsers() instead
   * Kept for backward compatibility
   */
  static async getAllProfiles(): Promise<User[]> {
    return UserService.getAllUsers();
  }
}

// Export individual functions for backward compatibility
export const getUser = UserService.getUser;
export const getUserByEmail = UserService.getUserByEmail;
export const getOrganizationUsers = UserService.getOrganizationUsers;
export const getOrganizationRoster = UserService.getOrganizationRoster;
export const getLoanOfficers = UserService.getLoanOfficers;
export const getCurrentUser = UserService.getCurrentUser;
export const updateUser = UserService.updateUser;
export const createUser = UserService.createUser;
export const deleteUser = UserService.deleteUser;
export const setUserActive = UserService.setUserActive;
export const updateUserRoles = UserService.updateUserRoles;
export const searchUsers = UserService.searchUsers;
export const getIncompleteProfiles = UserService.getIncompleteProfiles;
export const updateProfilePhoto = UserService.updateProfilePhoto;
export const updateNotificationPreferences = UserService.updateNotificationPreferences;
export const isEmailAvailable = UserService.isEmailAvailable;
export const isWebsiteSlugAvailable = UserService.isWebsiteSlugAvailable;

// Default export for backward compatibility
export default UserService;

// Company Profile types (temporary until full migration)
export interface CompanyProfile {
  _id: string;
  organization_id: string;
  company_name: string;
  legal_name?: string;
  dba_names: string[];
  company_nmls_id: string;
  state_licenses: Array<{
    state: string;
    license_number: string;
    expiration_date?: string;
  }>;
  federal_ein?: string;
  headquarters_address: {
    street: string;
    suite?: string;
    city: string;
    state: string;
    zip: string;
  };
  main_phone: string;
  toll_free_phone?: string;
  fax?: string;
  main_email: string;
  support_email?: string;
  website: string;
  company_mortgage_application_link?: string;
  logo_url?: string;
  logo_light_url?: string;
  favicon_url?: string;
  equal_housing_lender: boolean;
  default_disclaimers: string[];
  pre_approval_disclaimer?: string;
  // Public Google Business review URL — editable by admins, used by send-review-request
  google_review_url?: string | null;
  facebook_url?: string | null;
  instagram_url?: string | null;
  linkedin_url?: string | null;
  tiktok_url?: string | null;
  // Encompass integration fields
  encompass_instance_id?: string;
  encompass_sync_enabled?: boolean;
  encompass_last_sync?: string;
  encompass_sync_schedule?: 'manual' | 'daily' | 'hourly';
  encompass_sync_settings?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface BranchOffice {
  _id: string;
  id: string;
  organization_id: string;
  company_profile_id?: string;
  branch_name: string;
  branch_code?: string;
  is_headquarters: boolean;
  address: {
    street: string;
    suite?: string;
    city: string;
    state: string;
    zip: string;
  };
  phone?: string;
  fax?: string;
  email?: string;
  branch_manager_id?: string;
  is_active?: boolean;
  created_at: string;
  updated_at: string;
}

// Legacy type for backward compatibility (will be removed later)
export interface UserProfile {
  id?: string; // Added for compatibility with user impersonation
  _id?: string; // Legacy MongoDB ID
  email: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  role?: string; // Deprecated, use roles array
  roles?: string[]; // Array of role slugs
  organization_id?: string;
  organization?: {
    _id: string;
    name: string;
  };
}
